import React, { Component } from 'react';
import { Button, Dimmer, Grid, Loader } from 'semantic-ui-react';
import axios, { CancelTokenSource } from 'axios';
import Input from 'components/final-form/Input';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { EntityStorageStatisticsDto, ManagedStorageEntity, StorageInfoDto } from 'ts-types/api.types';
import { FormState } from 'final-form';
import { getEntityStatistics, getStorageInfo, moveDataBaseResourcesToStorage } from 'services/storageManagementService';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import PxGrid from 'components/layout/PxGrid';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import Select from 'components/final-form/Select';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import createDecorator from 'final-form-calculate';
import { composeValidators, mustBeNumber, mustBePositiveNumber } from 'utils/validatorUtils';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';

interface Props extends MessageSourceProps {

}

interface State {
  storageInfo?: StorageInfoDto,
  selectedEntityStatistics?: EntityStorageStatisticsDto,
  transferredRecords: number,
  loadingStorageData: boolean,
  managedStorageEntity?: ManagedStorageEntity,
  transferDataLoaded: boolean,
  showTransferredDataMessage: boolean,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class StorageManagementView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      loadingStorageData: true,
      transferredRecords: 0,
      transferDataLoaded: true,
      showTransferredDataMessage: false,
      cancelTokenSource
    }

    getStorageInfo(cancelTokenSource)
        .then(response => {
          this.setState({
            storageInfo: response
          })
        })
        .catch(() => {
          this.setErrorMessage(this.props.getMessage("error.azureIsNotStarted"))
        })
        .finally(() => this.setState({loadingStorageData: false}));
  }

  handleManagedEntityChange = ({values}: FormState) => {
    const {cancelTokenSource, managedStorageEntity, storageInfo} = this.state;
    const storageInfoUndefined = storageInfo === undefined;

    if (values.managedStorageEntity !== undefined && managedStorageEntity !== values.managedStorageEntity) {
      getEntityStatistics(values.managedStorageEntity, storageInfoUndefined, cancelTokenSource).then(response => {
        this.setState({
          managedStorageEntity: values.managedStorageEntity,
          selectedEntityStatistics: response
        });
      })
    }
  }

  quantityDecorator = createDecorator({
    field: "quantity",
    updates: {
      disableSubmit: currentQuantity => {
        const {selectedEntityStatistics} = this.state;

        if (selectedEntityStatistics) {
          const {databaseResourcesCount} = selectedEntityStatistics;
          return currentQuantity > databaseResourcesCount;
        }
        return false;
      }
    }
  });

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleSubmitTransferToAzure = async (values: any) => {

    const {selectedEntityStatistics, managedStorageEntity, cancelTokenSource} = this.state;
    let quantity = values.quantity;

    if (quantity === undefined) {
      if (selectedEntityStatistics) {
        quantity = selectedEntityStatistics.databaseResourcesCount;
      }
    }

    if (selectedEntityStatistics) {
      if (selectedEntityStatistics.databaseResourcesCount
          && managedStorageEntity) {

        this.setState({
          transferDataLoaded: false
        })

        await moveDataBaseResourcesToStorage(managedStorageEntity, quantity, cancelTokenSource)
            .finally(() => {

              const databaseRecords = selectedEntityStatistics.databaseResourcesCount - parseInt(quantity);
              const azureRecords = selectedEntityStatistics.storageResourcesCount + parseInt(quantity);

              this.setState({
                transferredRecords: quantity,
                selectedEntityStatistics: {
                  databaseResourcesCount: databaseRecords,
                  storageResourcesCount: azureRecords
                },
                transferDataLoaded: true,
                showTransferredDataMessage: true
              });
            });

        setTimeout(() => {
          this.setState({
            showTransferredDataMessage: false
          });
        }, 2000);
      }
    }
  }

  renderStorageData = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {storageInfo, transferDataLoaded, selectedEntityStatistics, errorMessage} = this.state;

    const managedStorageEntities = storageInfo
        ? storageInfo.managedStorageEntities
        : ['TEST_ATTACHMENTS', 'TEST_CERTIFICATES', 'VACCINATION_CERTIFICATES', 'AUTOMATIC_BILL_RUN', 'VMDL_EXPORT',
          'VMDL_DOWNSTREAM', 'EMAILS', 'NOTIFICATION_REPORTS', 'PATIENT_MEDICAL_DOCUMENT', 'TEST_RESULT'];

    const mappedManagedEntities = managedStorageEntities.map(managedStorageEntity => (
        {
          key: managedStorageEntity,
          text: getMessage(`storageManagement.select.${managedStorageEntity}`),
          value: managedStorageEntity
        }
    ));


    if (!storageInfo) {
      return (
          <>
            {!transferDataLoaded
                ? this.renderTransferringDataLoader()
                : <PxGrid>
                  {errorMessage &&
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  }
                  <Grid.Row>
                    <Grid.Column width={4} verticalAlign="middle">
                      {getMessage("managedEntity")}:
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <FinalForm
                          onSubmit={() => {
                          }}
                          render={() => (
                              <>
                                <Field
                                    name="managedStorageEntity"
                                    component={Select}
                                    fluid
                                    options={mappedManagedEntities}
                                />
                                <FormSpy subscription={{values: true}} onChange={this.handleManagedEntityChange}/>
                              </>
                          )}
                          initialValues={{managedStorageEntity: this.state.managedStorageEntity}}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  {this.renderSelectedEntityStatistics()}
                </PxGrid>
            }
          </>

      );
    }

    return (
        <>
          {!transferDataLoaded
              ? this.renderTransferringDataLoader()
              : <PxGrid>
                <Grid.Row>
                  <Grid.Column width={4}>{getMessage("info.accountType")}</Grid.Column>
                  <Grid.Column width={4}>{storageInfo.accountType}</Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>{getMessage("info.additionalInfo")}</Grid.Column>
                  <Grid.Column width={4}>{storageInfo.additionalInformation}</Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4} verticalAlign="middle">
                    {getMessage("managedEntity")}:
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FinalForm
                        onSubmit={() => {
                        }}
                        render={() => (
                            <>
                              <Field
                                  name="managedStorageEntity"
                                  component={Select}
                                  fluid
                                  options={mappedManagedEntities}
                              />
                              <FormSpy subscription={{values: true}} onChange={this.handleManagedEntityChange}/>
                            </>
                        )}
                        initialValues={{managedStorageEntity: this.state.managedStorageEntity}}
                    />
                  </Grid.Column>
                </Grid.Row>
                {this.renderSelectedEntityStatistics()}
              </PxGrid>
          }
        </>

    );
  }

  renderSelectedEntityStatistics(): React.ReactNode {
    const {getMessage} = this.props;
    const {selectedEntityStatistics, storageInfo} = this.state;
    if (!selectedEntityStatistics) {
      return null;
    }
    return (
        <>
          <Grid.Row>
            <Grid.Column width={4}>{getMessage("entity.databaseResourcesCount")}</Grid.Column>
            <Grid.Column width={4}>{selectedEntityStatistics.databaseResourcesCount}</Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>{getMessage("entity.storageResourcesCount")}</Grid.Column>
            <Grid.Column width={4}>
              {storageInfo ? selectedEntityStatistics.storageResourcesCount : getMessage("entity.unknown")}
            </Grid.Column>
          </Grid.Row>
          {storageInfo &&
              <Grid.Row>
                <Grid.Column>
                  <FinalForm
                      onSubmit={(values) => this.handleSubmitTransferToAzure(values)}
                      render={this.renderForm}
                      decorators={[this.quantityDecorator]}
                      initialValues={{disableSubmit: false}}
                      subscription={{values: true, submitting: true, hasValidationErrors: true}}
                  />
                </Grid.Column>
              </Grid.Row>
          }
        </>
    );
  }

  render(): React.ReactNode {
    const {loadingStorageData} = this.state;

    return (
        <PraxStyledSegment>
          <StyledViewTableRow>
            {loadingStorageData ? this.renderLoader() : this.renderStorageData()}
          </StyledViewTableRow>
        </PraxStyledSegment>
    );
  }

  renderForm = ({handleSubmit, values, submitting, hasValidationErrors}: FormRenderProps): React.ReactNode => {
    const {getMessage, getMessageWithNamedParams} = this.props;
    const {selectedEntityStatistics, transferredRecords} = this.state;

    let noRecordsDisableButton = false;
    if (selectedEntityStatistics) {
      noRecordsDisableButton = selectedEntityStatistics.databaseResourcesCount == 0;
    }

    return (
        <form onSubmit={handleSubmit} style={{marginLeft: "-5px"}}>
          <FormContent>
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column width={4}>{getMessage("info.numberOfRecords")}</Grid.Column>
                <Grid.Column width={1}>
                  <Field
                      name="quantity"
                      component={Input}
                      fluid
                      validate={composeValidators(mustBeNumber, mustBePositiveNumber)}
                  />
                </Grid.Column>
                <Grid.Column width={2}>
                  <Button
                      compact
                      type="submit"
                      primary
                      disabled={values.disableSubmit || submitting || hasValidationErrors || noRecordsDisableButton}
                  >{getMessage("button.transferToAzure")}
                  </Button>
                </Grid.Column>
                <Grid.Column width={2} verticalAlign={'middle'}>
                  {
                    this.state.showTransferredDataMessage &&
                    <div
                        style={{
                          fontsize: "2rem",
                          color: "darkgreen"}}>
                      {getMessageWithNamedParams("storageManagement.dataTransferredSuccess", {numberOfRecords: transferredRecords})}
                    </div>
                  }
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
          </FormContent>
        </form>
    )
  }

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("storageManagement.loader")}</Loader>
        </Dimmer>
    );
  };

  renderTransferringDataLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("storageManagement.transferringData.loader")}</Loader>
        </Dimmer>
    );
  }
}

export default withMessages(StorageManagementView);

